/**
 * @license
 * Copyright 2021 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import * as params from "./params";

/**
 * How long to wait for the camera stream to produce frames before giving up.
 * A hang here used to leave a blank page forever; failing loudly is better.
 */
const STREAM_READY_TIMEOUT_MS = 15000;

/**
 * Resolves once the video is actually producing frames, i.e. once it reports
 * real dimensions.
 *
 * `loadedmetadata` alone is not a safe signal. iOS Safari does not report
 * metadata for a MediaStream until playback has actually started, and in some
 * builds the event does not arrive at all even though frames are flowing, so
 * this listens to several events AND polls the dimensions, whichever comes
 * first. Rejects rather than hanging, so the failure is visible.
 */
function waitForFrames(video, timeoutMs) {
  const hasFrames = () => video.videoWidth > 0 && video.videoHeight > 0;
  if (hasFrames()) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const events = ["loadedmetadata", "loadeddata", "canplay", "playing"];
    let done = false;

    const cleanup = () => {
      clearInterval(poll);
      clearTimeout(deadline);
      for (const name of events) {
        video.removeEventListener(name, check);
      }
    };
    const check = () => {
      if (done || !hasFrames()) {
        return;
      }
      done = true;
      cleanup();
      resolve();
    };

    const poll = setInterval(check, 100);
    const deadline = setTimeout(() => {
      if (done) {
        return;
      }
      done = true;
      cleanup();
      reject(
        new Error(
          `Camera stream produced no frames within ${timeoutMs / 1000}s. ` +
            "On iOS this usually means the video element was not allowed to " +
            "play inline."
        )
      );
    }, timeoutMs);

    for (const name of events) {
      video.addEventListener(name, check);
    }
    check();
  });
}

export function isiOS() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export function isAndroid() {
  return /Android/i.test(navigator.userAgent);
}

export function isMobile() {
  return isAndroid() || isiOS();
}

export class Camera {
  constructor() {
    this.video = document.getElementById("video");
  }

  /**
   * Initiate a Camera instance and wait for the camera stream to be ready.
   * @param cameraParam From app `STATE.camera`.
   */
  static async setup(cameraParam) {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error(
        "Browser API navigator.mediaDevices.getUserMedia not available"
      );
    }

    const { targetFPS, sizeOption, mobileSizeOption } = cameraParam;
    const $size = params.VIDEO_SIZE[sizeOption];
    // A phone gets its own, smaller option: see STATE.camera in params.js for
    // what that costs and why it is overridable.
    const $mobile =
      params.VIDEO_SIZE[mobileSizeOption] || params.VIDEO_SIZE["360 X 270"];
    const videoConfig = {
      audio: false,
      video: {
        facingMode: "user",
        // Only setting the video to a specified size for large screen, on
        // mobile devices accept the default size.
        width: isMobile() ? $mobile.width : $size.width,
        height: isMobile() ? $mobile.height : $size.height,
        frameRate: {
          ideal: targetFPS,
        },
      },
    };

    const stream = await navigator.mediaDevices.getUserMedia(videoConfig);
    const camera = new Camera();
    camera.video.srcObject = stream;

    // iOS Safari refuses to start an inline video that is not muted. Set these
    // on the element as well as in the markup, so the guarantee does not depend
    // on the attributes surviving.
    camera.video.muted = true;
    camera.video.playsInline = true;
    camera.video.setAttribute("playsinline", "");
    camera.video.setAttribute("muted", "");

    // play() must come BEFORE waiting for readiness. iOS does not report
    // metadata for a MediaStream until playback has started, so waiting first
    // and playing afterwards deadlocks: the wait is for an event that only
    // playing can produce. Rejection is left to the readiness timeout, which
    // gives a far more useful message than a bare AbortError.
    const playing = camera.video.play();
    if (playing && typeof playing.catch === "function") {
      playing.catch(() => {});
    }

    await waitForFrames(camera.video, STREAM_READY_TIMEOUT_MS);

    const videoWidth = camera.video.videoWidth;
    const videoHeight = camera.video.videoHeight;
    // Must set below two lines, otherwise video element doesn't show.
    camera.video.width = videoWidth;
    camera.video.height = videoHeight;

    // Aspect ratio rather than fixed pixels: the wrapper is width-driven so the
    // view scales to the screen, and CSS derives the height. Assigning to
    // .style.aspectRatio also avoids clobbering any other inline style, which
    // `element.style = "..."` would.
    const canvasContainer = document.querySelector(".canvas-wrapper");
    canvasContainer.style.aspectRatio = `${videoWidth} / ${videoHeight}`;

    return camera;
  }
}
